import { ReactElement, ReactNode, useEffect, useState } from "react";
import { AuthContextProps, useAuth } from "react-oidc-context";
import { UserInfoModel } from "app/model/ua/user-info";
import { AuthenticationService } from "app/services/common/AuthenticationService";
import { HttpStatusCode } from "axios";
import { clearUserInfo, setUserInfo } from "app/reducers/userSlice";
import { useAppDispatch, useAppSelector } from "app/store/hook";
import { showAndHideSpinner } from "app/reducers/spinnerSlice";
import AuthProtectorLoading from "app/guard/AuthProtectorLoading";

interface AppProtectorProps {
  children: ReactNode;
}
export default function AuthProtector(props: AppProtectorProps): ReactElement {
  const { children } = props;
  const dispatch = useAppDispatch();
  const auth: AuthContextProps = useAuth();
  const userInfo: UserInfoModel = useAppSelector((state) => state.userInfo.userInfo);

  const fetchCurrentUserInfo = () => {
    dispatch(showAndHideSpinner(true));
    let newUserInfo: UserInfoModel | null = null;
    try {
      AuthenticationService.getInstance().getUserInfo()
      .then((response) => {
        if (response && response.data && response.statusCode === HttpStatusCode.Ok) {
          newUserInfo = new UserInfoModel(response.data);
        } else {
          newUserInfo = null;
        }
      })
      .catch(() => {
        newUserInfo = null;
      })
      .finally(() => {
        if (newUserInfo) {
          dispatch(setUserInfo(newUserInfo));
        } else {
          dispatch(clearUserInfo());
        }
      });
    } catch {
      dispatch(clearUserInfo());
    } finally {
      dispatch(showAndHideSpinner(false));
    }
  };

  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  useEffect(() => {
    setIsAuthorized(false);
    if (!auth.error && !auth.isLoading) {
      if (auth.isAuthenticated) {
        if (userInfo && userInfo.userId) {
          if (auth.user?.profile.sub === userInfo.userId) {
            setIsAuthorized(true);
          } else {
            fetchCurrentUserInfo();
          }
        } else {
          fetchCurrentUserInfo();
        }
      } else {
        auth.signinRedirect().then(() => {}, () => {});
      }
    }
  }, [auth, userInfo]);

  switch (auth.activeNavigator) {
    case "signinRedirect":
    case "signoutRedirect":
    case "signoutSilent":
      return <AuthProtectorLoading message="로그인 화면으로 이동 중입니다."/>;
  }

  if (auth.isAuthenticated && isAuthorized) {
    return (<>{children}</>);
  }

  return <AuthProtectorLoading message="사용자 정보를 불러오고 있습니다." />;
}
